import LandingShell from '@/components/landing/LandingShell';
import { MAPLEOS_PRODUCT_MAP, MAPLEOS_PRODUCTS, formatUsdFromCents } from '@/components/billing/mapleosProducts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import SEO from '@/components/common/SEO';
import { ArrowRight, HardDriveDownload, Monitor, Rocket } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAICredits } from '@/hooks/useAICredits';

export default function ProductDetails() {
  const { productId } = useParams<{ productId: keyof typeof MAPLEOS_PRODUCT_MAP }>();
  const product = productId ? MAPLEOS_PRODUCT_MAP[productId] : null;
  const { startProductCheckout, productLoading } = useAICredits();

  if (!product) {
    return <Navigate to="/purchase" replace />;
  }

  const comparableProducts = MAPLEOS_PRODUCTS.filter((item) => item.id !== product.id);

  return (
    <LandingShell>
      <SEO
        title={product.name}
        description={`${product.name} — ${product.tagline}`}
      />

      <div className="container mx-auto max-w-6xl px-4 py-10">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge>{product.name}</Badge>
                <Badge variant="outline">Annual release</Badge>
                {product.includesBareMetalInstaller && (
                  <Badge variant="secondary">Bare metal installer included</Badge>
                )}
              </div>
              <h1 className="text-4xl font-bold tracking-tight">{product.name}</h1>
              <p className="max-w-3xl text-lg text-muted-foreground">{product.description}</p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>What you get</CardTitle>
                <CardDescription>{product.audience}</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2">
                {product.features.map((feature) => (
                  <div key={feature} className="rounded-xl border bg-card px-4 py-3 text-sm">
                    {feature}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Installer access</CardTitle>
                <CardDescription>
                  Every edition includes web and desktop installers. Pro and Enterprise also include the bare metal installer.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-3">
                <div className="rounded-xl border p-4">
                  <div className="flex items-center gap-2 font-medium">
                    <Monitor className="h-4 w-4 text-primary" /> Web
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">Included in every MapleOS 26 edition.</p>
                </div>
                <div className="rounded-xl border p-4">
                  <div className="flex items-center gap-2 font-medium">
                    <Rocket className="h-4 w-4 text-primary" /> Desktop installer
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">Included in every MapleOS 26 edition.</p>
                </div>
                <div className="rounded-xl border p-4">
                  <div className="flex items-center gap-2 font-medium">
                    <HardDriveDownload className="h-4 w-4 text-primary" /> Bare metal installer
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {product.includesBareMetalInstaller
                      ? 'Included with this edition.'
                      : 'Available in Pro 26 and Enterprise 26.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>{product.name}</CardTitle>
                <CardDescription>{product.tagline}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <div className="text-4xl font-bold">
                    {product.priceCents === 0 ? product.priceLabel : formatUsdFromCents(product.priceCents)}
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {product.priceCents === 0 ? 'Web and desktop installer included' : 'One-time annual edition purchase'}
                  </div>
                </div>
                <Button className="w-full" onClick={() => startProductCheckout(product)} disabled={productLoading === product.id}>
                  {product.ctaLabel}
                </Button>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/purchase">Compare all MapleOS products</Link>
                </Button>
                <div className="rounded-xl border bg-muted/30 p-4 text-sm text-muted-foreground">
                  AI usage is billed separately through credits. Trial credits are included and auto top-up can be enabled later inside MapleOS or on the{' '}
                  <Link to="/credits" className="font-medium text-foreground underline-offset-4 hover:underline">
                    credits page
                  </Link>
                  .
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Compare editions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {comparableProducts.map((item) => (
                  <Link
                    key={item.id}
                    to={`/products/${item.id}`}
                    className="flex items-center justify-between rounded-xl border px-4 py-3 text-sm transition hover:border-primary hover:bg-muted/30"
                  >
                    <div>
                      <div className="font-medium">{item.name}</div>
                      <div className="text-muted-foreground">{item.priceLabel}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </Link>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </LandingShell>
  );
}
